import React, { useState, useEffect } from "react";
import api from "../../api/axios";
import { ToastContainer, toast } from "react-toastify";
import FrenchDatePicker from "./FrenchDatePicker";
import Input from "../ui/Input";
import Button from "../ui/Button";
import ModelSelect from "./ModelSelect";

const EffectiveDirect = ({ url = "effective_standard" }) => {
  const [data, setData] = useState({
    chain: "",
    model: "",
    machinistes: "",
    machinistes_stagiaires: "",
    repassage_preparation: "",
    trassage: "",
    transport: "",
    autre: "",
    machines_speciales: "",
    trassage_special: "",
    controle_table: "",
    controle_final: "",
    machinistes_retouche: "",
    repassage_final: "",
    finition: "",
    start_date: "",
    end_date: "",
    custom_date: new Date(), // Start with current date
    cointa: "",
    price_by_part: "",
  });

  const [chains, setChains] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [effectiveStandardId, setEffectiveStandardId] = useState(null);
  const [isFetching, setIsFetching] = useState(false); // Flag to prevent infinite loops
  const [total, setTotal] = useState(0);

  const calculateTotal = (effectifdata) => {
    console.log("🧮 calculateTotal called with:", effectifdata);
    const sum =
      (parseFloat(effectifdata.machinistes) || 0) +
      (parseFloat(effectifdata.machinistes_stagiaires) || 0) +
      (parseFloat(effectifdata.repassage_preparation) || 0) +
      (parseFloat(effectifdata.trassage) || 0) +
      (parseFloat(effectifdata.transport) || 0) +
      (parseFloat(effectifdata.autre) || 0) +
      (parseFloat(effectifdata.machines_speciales) || 0) +
      (parseFloat(effectifdata.trassage_special) || 0) +
      (parseFloat(effectifdata.controle_table) || 0) +
      (parseFloat(effectifdata.controle_final) || 0) +
      (parseFloat(effectifdata.machinistes_retouche) || 0) +
      (parseFloat(effectifdata.repassage_final) || 0) +
      (parseFloat(effectifdata.finition) || 0);
    console.log("🧮 Calculated sum:", sum);
    setTotal(sum);
  };

  useEffect(() => {
    console.log("🚀 Component mounted, initial data state:", data);
    console.log("🚀 URL:", url);
    console.log("🚀 Calling fetchModels and fetchChains");
    
    const fetchModels = async () => {
      console.log("📋 fetchModels called");
      try {
        const response = await api.get("/models");
        setModels(response.data);
        console.log("📋 Models loaded:", response.data);
      } catch (error) {
        toast.error("Erreur lors de la récupération des modèles.");
      }
    };

    const fetchChains = async () => {
      console.log("🔗 fetchChains called");
      try {
        const response = await api.get("/chains");
        setChains(response.data);
        console.log("🔗 Chains loaded:", response.data);
      } catch (error) {
        toast.error("Erreur lors de la récupération des chaînes.");
      }
    };

    fetchModels();
    fetchChains();
  }, []);

  // Debug useEffect to log data state changes
  useEffect(() => {
    console.log("🔄 Data state changed:", data);
  }, [data]);

  // Debug useEffect to log isFetching state changes
  useEffect(() => {
    console.log("🔄 isFetching state changed:", isFetching);
  }, [isFetching]);

  // Debug useEffect to log effectiveStandardId state changes
  useEffect(() => {
    console.log("🔄 effectiveStandardId state changed:", effectiveStandardId);
  }, [effectiveStandardId]);

  // Debug useEffect to log total state changes
  useEffect(() => {
    console.log("🔄 total state changed:", total);
  }, [total]);

  // Debug useEffect to log url prop changes
  useEffect(() => {
    console.log("🔄 url prop changed:", url);
  }, [url]);

  // Debug useEffect to log chains state changes
  useEffect(() => {
    console.log("🔄 chains state changed:", chains);
  }, [chains]);

  // Debug useEffect to log models state changes
  useEffect(() => {
    console.log("🔄 models state changed:", models);
  }, [models]);

  // Debug useEffect to log loading state changes
  useEffect(() => {
    console.log("🔄 loading state changed:", loading);
  }, [loading]);

  const onChange = (name, value) => {
    console.log("🔄 onChange called");
    console.log("🔄 onChange called:", { name, value, url });
    
    // Convert numeric fields to numbers
    const numericFields = [
      "machinistes",
      "machinistes_stagiaires",
      "repassage_preparation",
      "trassage",
      "transport",
      "autre",
      "machines_speciales",
      "trassage_special",
      "controle_table",
      "controle_final",
      "machinistes_retouche",
      "repassage_final",
      "finition",
      "price_by_part",
    ];
  
    const newValue = numericFields.includes(name)
      ? Number(value) || ""  
      : value;
  
    console.log("🔄 Updating state with new value:", { name, newValue });
    setData({
      ...data,
      [name]: newValue,
    });

    // If chain or model changes, clear form fields but preserve the date
    if ((name === "chain" || name === "model") && url === "effective_real") {
      console.log("🔗 Chain or model changed, clearing form fields");
      console.log("🔗 Current data state:", data);
      console.log("🔗 New value:", newValue);
      
        // Clear effectif fields but preserve selections and date
        setData(prevData => {
          const updatedData = {
            ...prevData,
            [name]: newValue,
            // Keep the date when chain or model changes
            custom_date: prevData.custom_date,
            // Clear effectif fields
            machinistes: "",
            machinistes_stagiaires: "",
            repassage_preparation: "",
            trassage: "",
            transport: "",
            autre: "",
            machines_speciales: "",
            trassage_special: "",
            controle_table: "",
            controle_final: "",
            machinistes_retouche: "",
            repassage_final: "",
            finition: "",
          };
          
          console.log("🔄 Form fields cleared, updated data:", updatedData);
          console.log("🧹 Clearing form after chain/model change");
          
          // If we have all three required fields and not currently fetching, fetch data
          if (updatedData.custom_date && !isFetching) {
            console.log("🔍 Triggering fetch after chain/model change");
            console.log("🔍 Updated data for fetch:", updatedData);
            // Fetch data immediately with the updated data
            fetchDataByChainModelAndDate(updatedData);
          } else {
            console.log("⏸️ Skipping fetch - missing date or already fetching", {
              hasDate: !!updatedData.custom_date,
              isFetching: isFetching
            });
          }
          
          return updatedData;
        });
      
      setTotal(0);
    }
  };
  
  const handleDateChange = (date, name) => {
    console.log("📅 handleDateChange called");
    console.log("📅 handleDateChange called:", { name, date, url });
    
    const adjustedDate = new Date(date);
    adjustedDate.setHours(12, 0, 0, 0);
    
    console.log("🔄 Setting new date:", adjustedDate);
    
    // If this is the custom_date change and we're in effective_real mode with model and chain selected,
    // automatically fetch data for the new date immediately with the new date value
    if (name === "custom_date" && url === "effective_real" && data.model && data.chain && !isFetching) {
      console.log("🔍 Date changed, triggering fetch for new date immediately");
      console.log("🔍 Current data state:", data);
      console.log("🔍 New date:", adjustedDate);
      
      // Create updated data object with the new date
      const updatedData = {
        ...data,
        [name]: adjustedDate,
      };
      
      // Fetch data immediately with the updated data
      console.log("🚀 Triggering immediate fetch with updated data:", updatedData);
      fetchDataByChainModelAndDate(updatedData);
    } else if ((name === "start_date" || name === "end_date") && url === "effective_standard") {
      console.log("📅 Standard date changed, updating state only");
      // For effective_standard dates, just update the state
      // The useEffect will handle the data fetching
    } else {
      console.log("⏸️ Skipping fetch - conditions not met:", {
        isCustomDate: name === "custom_date",
        isEffectiveReal: url === "effective_real",
        hasModel: !!data.model,
        hasChain: !!data.chain,
        isFetching: isFetching
      });
    }
    
    // Update the state after the fetch logic
    console.log("🔄 Updating state with new date:", adjustedDate);
    setData({
      ...data,
      [name]: adjustedDate,
    });
  };

  // Function to fetch data by chain, model, and date
  const fetchDataByChainModelAndDate = async (useData = null) => {
    console.log("🔍 fetchDataByChainModelAndDate called");
    const currentData = useData || data;
    
    console.log("🔍 fetchDataByChainModelAndDate called with:", {
      model: currentData.model,
      chain: currentData.chain,
      custom_date: currentData.custom_date,
      url: url,
      isFetching: isFetching,
      useDataProvided: !!useData,
      useDataValue: useData
    });

    if (isFetching) {
      console.log("⏸️ Already fetching, skipping. isFetching value:", isFetching);
      return;
    }

    if (!currentData.model || !currentData.chain || !currentData.custom_date) {
      console.log("❌ Missing required fields, skipping fetch");
      return; // Don't fetch if any of the three required fields is missing
    }

    setIsFetching(true);
    console.log("🚀 Starting fetch... isFetching set to true");

    try {
      const dateStr = currentData.custom_date.toISOString().split("T")[0];
      console.log("📅 Fetching data for date:", dateStr);
      console.log("📅 Original date object:", currentData.custom_date);
      console.log("📅 Date toISOString():", currentData.custom_date.toISOString());
      
      const response = await api.get(`/effective_real_by_date/${currentData.model}/${currentData.chain}`, {
        params: { date: dateStr }
      });

      console.log("📡 API Response:", response.data);

      if (response.data && response.data.effectifDirects.length > 0) {
        // Data exists - prefill the form
        console.log("✅ Data found, prefilling form");
        console.log("🔍 API Response includes ID:", response.data.id);
        const effectifDirect = response.data.effectifDirects[0];
        
        setData(prevData => {
          console.log("🔄 Setting data with existing values, preserving:", {
            chain: prevData.chain,
            model: prevData.model,
            custom_date: prevData.custom_date
          });
          
          const updatedData = {
            ...prevData,
            machinistes: effectifDirect.machinistes || "",
            machinistes_stagiaires: effectifDirect.machinistes_stagiaires || "",
            repassage_preparation: effectifDirect.repassage_preparation || "",
            trassage: effectifDirect.trassage || "",
            transport: effectifDirect.transport || "",
            autre: effectifDirect.autre || "",
            machines_speciales: effectifDirect.machines_speciales || "",
            trassage_special: effectifDirect.trassage_special || "",
            controle_table: effectifDirect.controle_table || "",
            controle_final: effectifDirect.controle_final || "",
            machinistes_retouche: effectifDirect.machinistes_retouche || "",
            repassage_final: effectifDirect.repassage_final || "",
            finition: effectifDirect.finition || "",
          };
          
          console.log("✅ Final updated data after prefilling:", updatedData);
          return updatedData;
        });
        
        calculateTotal(effectifDirect);
        setEffectiveStandardId(response.data.id); // Set ID for update
        console.log("✅ Form prefilled, effectiveStandardId set to:", response.data.id);
        toast.success(`Données existantes récupérées pour le ${dateStr}`);
      } else {
        // No data exists - clear form fields but keep date, chain, and model
        console.log("❌ No data found, clearing form fields");
        console.log("🧹 Clearing form after no data found");
        clearFormFields();
        setEffectiveStandardId(null); // Clear ID for insert
        console.log("✅ Form cleared, ready for new data entry");
        toast.info(`Aucune donnée trouvée pour le ${dateStr} - Prêt pour nouvelle saisie`);
      }
    } catch (error) {
      console.error("❌ Error fetching data:", error);
      console.log("🧹 Clearing form after error");
      // On error, clear form but keep selections
      clearFormFields();
      setEffectiveStandardId(null);
      toast.error("Erreur lors de la récupération des données.");
    } finally {
      setIsFetching(false);
      console.log("🏁 Fetch completed, isFetching set to false");
    }
  };

  // Function to clear form fields but preserve the date, chain, and model
  const clearFormFields = () => {
    console.log("🧹 clearFormFields called");
    console.log("🧹 clearFormFields called, preserving:", {
      chain: data.chain,
      model: data.model,
      custom_date: data.custom_date
    });
    
    setData(prevData => {
      console.log("🔄 Clearing form fields, keeping:", {
        chain: prevData.chain,
        model: prevData.model,
        custom_date: prevData.custom_date
      });
      
      const clearedData = {
        ...prevData,
        // Keep the chain, model, and date
        chain: prevData.chain,
        model: prevData.model,
        custom_date: prevData.custom_date, // Keep the date
        // Keep other settings
        start_date: prevData.start_date,
        end_date: prevData.end_date,
        cointa: prevData.cointa,
        price_by_part: prevData.price_by_part,
        // Clear only the effectif fields
        machinistes: "",
        machinistes_stagiaires: "",
        repassage_preparation: "",
        trassage: "",
        transport: "",
        autre: "",
        machines_speciales: "",
        trassage_special: "",
        controle_table: "",
        controle_final: "",
        machinistes_retouche: "",
        repassage_final: "",
        finition: "",
      };
      
      console.log("🧹 Final cleared data:", clearedData);
      return clearedData;
    });
    setTotal(0);
    console.log("✅ Form fields cleared, total reset to 0, date preserved");
  };

  // Function to clear entire form including date (for starting fresh)
  const clearEntireForm = () => {
    console.log("🧹 clearEntireForm called - clearing everything");
    console.log("🧹 Current data state before clearing:", data);
    setData(prevData => {
      const clearedData = {
        ...prevData,
        // Clear everything except basic structure
        chain: "",
        model: "",
        custom_date: new Date(), // Reset to current date
        start_date: "",
        end_date: "",
        cointa: "",
        price_by_part: "",
        // Clear effectif fields
        machinistes: "",
        machinistes_stagiaires: "",
        repassage_preparation: "",
        trassage: "",
        transport: "",
        autre: "",
        machines_speciales: "",
        trassage_special: "",
        controle_table: "",
        controle_final: "",
        machinistes_retouche: "",
        repassage_final: "",
        finition: "",
      };
      
      console.log("🧹 Final cleared data:", clearedData);
      return clearedData;
    });
    setTotal(0);
    setEffectiveStandardId(null);
    console.log("✅ Entire form cleared, ready for fresh start");
  };

  const handleModelChange = async (e) => {
    console.log("🔧 handleModelChange called");
    const model = e.target.value;
    console.log("🔧 handleModelChange called with:", model);
    // Use the onChange function to ensure consistent behavior and trigger fetch logic
    onChange("model", model);
  };

  useEffect(() => {
    const checkExisted = async () => {
      console.log('🔍 checkExisted called');
      console.log('🔍 checkExisted called for:', url);
      console.log('🔍 Current data state:', {
        model: data.model,
        chain: data.chain,
        start_date: data.start_date,
        end_date: data.end_date
      });
      
      // Only run this for effective_standard, not for effective_real
      if (url !== "effective_standard") {
        console.log('⏸️ Skipping checkExisted for:', url);
        return;
      }
      
      if (
        data.model &&
        data.chain &&
        data.start_date && data.start_date !== "" &&
        data.end_date && data.end_date !== ""
      ) {
        console.log('✅ All required fields present, checking existing data');
        try {
          const response = await api.get(`/effective_real_date/${data.model}`, {
            params: {
              start_date: data.start_date.toISOString().split("T")[0],
              end_date: data.end_date.toISOString().split("T")[0],
              chain: data.chain,
            },
          });

          if (response.data) {
            const effectiveRealData = response.data;
            const effectifDirects =
              effectiveRealData.effectif_directs || null;

            if (effectifDirects != null) {
              console.log('✅ Found existing data, prefilling form');
              setData(prevData => ({
                ...prevData,
                chain: effectiveRealData.chain || "",
                model: effectiveRealData.model,
                machinistes: effectifDirects.machinistes || "",
                machinistes_stagiaires:
                  effectifDirects.machinistes_stagiaires || "",
                repassage_preparation:
                  effectifDirects.repassage_preparation || "",
                trassage: effectifDirects.trassage || "",
                transport: effectifDirects.transport || "",
                autre: effectifDirects.autre || "",
                machines_speciales: effectifDirects.machines_speciales || "",
                trassage_special: effectifDirects.trassage_special || "",
                controle_table: effectifDirects.controle_table || "",
                controle_final: effectifDirects.controle_final || "",
                machinistes_retouche:
                  effectifDirects.machinistes_retouche || "",
                repassage_final: effectifDirects.repassage_final || "",
                finition: effectifDirects.finition || "",
                start_date: effectiveRealData.start_date || "",
                end_date: effectiveRealData.end_date || "",
                cointa: effectiveRealData.cointa || "",
                price_by_part: effectiveRealData.price_by_part || "",
              }));

              setEffectiveStandardId(effectiveRealData.id);
              calculateTotal(effectifDirects);
            } else {
              console.log("❌ No effectif_directs found");
            }
          } else {
            console.log("❌ No response data found");
          }
        } catch (error) {
          console.log("❌ Error in checkExisted:", error);
        }
      } else {
        console.log('⏸️ Missing required fields for effective_standard');
      }
    };

    console.log('🔄 useEffect triggered with dependencies:', {
      url,
      model: data.model,
      chain: data.chain,
      start_date: data.start_date,
      end_date: data.end_date
    });
    console.log('🔄 Calling checkExisted');
    checkExisted();
  }, [url, data.model, data.chain, data.start_date, data.end_date]); // Only run when these specific values change

  // Function to verify if the record still exists
  const verifyRecordExists = async () => {
    if (!effectiveStandardId || url !== "effective_standard") return true;
    
    try {
      await api.get(`/effective_standard/${effectiveStandardId}`);
      return true;
    } catch (error) {
      console.log("Record verification failed, clearing ID:", error);
      setEffectiveStandardId(null);
      return false;
    }
  };

  const handleSubmit = async (event) => {
    console.log("📝 handleSubmit called");
    setLoading(true);
    event.preventDefault();

    // Verify record exists before proceeding
    const recordExists = await verifyRecordExists();
    
    console.log("📝 handleSubmit called with data:", data);
    console.log("📝 URL:", url);
  
    let startDate = new Date(data.start_date);
    let endDate = new Date(data.end_date);
    
    console.log("📝 Start date:", startDate, "End date:", endDate);
  
    const formattedData = {
      start_date: data.start_date
        ? startDate.toISOString().split("T")[0]
        : "",
      end_date: data.end_date ? endDate.toISOString().split("T")[0] : "",
      ...(url === "effective_real" && { custom_date: data.custom_date ? data.custom_date.toISOString().split("T")[0] : "" }),
      cointa: data.cointa,
      price_by_part: typeof data.price_by_part === "number" ? data.price_by_part : "",
      chain: data.chain,
      model: data.model,
      effectif_directs: [
        {
          machinistes: typeof data.machinistes === "number" ? data.machinistes : "",
          machinistes_stagiaires: typeof data.machinistes_stagiaires === "number" ? data.machinistes_stagiaires : "",
          repassage_preparation: typeof data.repassage_preparation === "number" ? data.repassage_preparation : "",
          trassage: typeof data.trassage === "number" ? data.trassage : "",
          transport: typeof data.transport === "number" ? data.transport : "",
          autre: typeof data.autre === "number" ? data.autre : "",
          machines_speciales: typeof data.machines_speciales === "number" ? data.machines_speciales : "",
          trassage_special: typeof data.trassage_special === "number" ? data.trassage_special : "",
          controle_table: typeof data.controle_table === "number" ? data.controle_table : "",
          controle_final: typeof data.controle_final === "number" ? data.controle_final : "",
          machinistes_retouche: typeof data.machinistes_retouche === "number" ? data.machinistes_retouche : "",
          repassage_final: typeof data.repassage_final === "number" ? data.repassage_final : "",
          finition: typeof data.finition === "number" ? data.finition : "",
        },
      ],
      effectif_indirects: [],
    };

    calculateTotal(formattedData.effectif_directs[0])
    
    console.log("📝 Formatted data to be sent:", formattedData);

    try {
      if (effectiveStandardId && recordExists) {
        // Update existing record
        console.log("📝 Updating existing record with ID:", effectiveStandardId);
        const response = await api.post(`/${url}/${effectiveStandardId}`, formattedData);
        console.log("📝 Update response:", response.data);
        toast.success("Données mises à jour avec succès.");
      } else {
        // Insert new record
        console.log("📝 Inserting new record");
        const response = await api.post(`/${url}`, formattedData);
        console.log("📝 Insert response:", response.data);
        toast.success("Données enregistrées avec succès.");
      }
      // After successful save, clear form but preserve the date, chain, and model
      console.log("🧹 Clearing form after successful save");
      if (url === "effective_real") {
        // Clear form fields but preserve date, chain, and model
        setData(prevData => {
          const clearedData = ({
            ...prevData,
            // Keep chain, model, and date
            chain: prevData.chain,
            model: prevData.model,
            custom_date: prevData.custom_date, // Keep the date
            // Clear effectif fields
            machinistes: "",
            machinistes_stagiaires: "",
            repassage_preparation: "",
            trassage: "",
            transport: "",
            autre: "",
            machines_speciales: "",
            trassage_special: "",
            controle_table: "",
            controle_final: "",
            machinistes_retouche: "",
            repassage_final: "",
            finition: "",
          });
          
          console.log("✅ Form cleared after save, final data:", clearedData);
          return clearedData;
        });
        setTotal(0);
        console.log("✅ Form cleared after save, date preserved");
      } else {
        // For effective_standard, clear everything including date
        const clearedData = {
          chain: "",
          model: "",
          machinistes: "",
          machinistes_stagiaires: "",
          repassage_preparation: "",
          trassage: "",
          transport: "",
          autre: "",
          machines_speciales: "",
          trassage_special: "",
          controle_table: "",
          controle_final: "",
          machinistes_retouche: "",
          repassage_final: "",
          finition: "",
          start_date: "",
          end_date: "",
          custom_date: new Date(), // Reset to current date
          cointa: "",
          price_by_part: "",
        };
        
        console.log("✅ Form cleared after save (effective_standard), final data:", clearedData);
        setData(clearedData);
      }
      setEffectiveStandardId(null);
      setTotal(0)
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement des données.", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async () => {
    console.log("🗑️ handleDelete called");
    try {
      await api.delete(`/${url}/${effectiveStandardId}`);
      toast.success("Données supprimées avec succès.");
      console.log("🧹 Clearing form after successful delete");
      // Clear form but preserve the date
      if (url === "effective_real") {
        console.log("🗑️ Deleting effective_real, calling clearFormFields");
        clearFormFields();
      } else {
        // For effective_standard, clear everything including date
        const clearedData = {
          chain: "",
          model: "",
          machinistes: "",
          machinistes_stagiaires: "",
          repassage_preparation: "",
          trassage: "",
          transport: "",
          autre: "",
          machines_speciales: "",
          trassage_special: "",
          controle_table: "",
          controle_final: "",
          machinistes_retouche: "",
          repassage_final: "",
          finition: "",
          start_date: "",
          end_date: "",
          custom_date: new Date(), // Reset to current date
          cointa: "",
          price_by_part: "",
        };
        
        console.log("🗑️ Deleting effective_standard, setting cleared data:", clearedData);
        setData(clearedData);
      }
      setEffectiveStandardId(null);
      setTotal(0)
    } catch (error) {
      toast.error("Erreur lors de la suppression des données.", error);
    }
  };



  return (
    <div className="ml-[16.66%] mr-5 pt-[6rem]">
      <ToastContainer />
      <form className="ml-7" onSubmit={handleSubmit}>
        <h2 className="text-2xl font-semibold my-5 mb-[3rem]">
          {url === "effective_real"
            ? "Effectif Réel Direct"
            : "Effectif Standard Direct"}
            <span className="text-xl font-normal">- Total: {total}</span>
        </h2>
        <div className="mb-4">
          <label className="block font-semibold">Chaîne</label>
          <select
            className="block w-full mt-4 outline-0 p-[.5rem] border border-[#b3b3b3] focus:border-2 focus:border-[#2684ff] rounded"
            value={data.chain}
            onChange={(e) => {
              console.log("🔗 Chain select changed:", e.target.value);
              onChange("chain", e.target.value);
            }}
          >
            <option value="">Sélectionner une chaîne</option>
            {chains.map((chain, index) => (
              <option key={index} value={chain.name}>
                {chain.name}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="block font-semibold">Modèle</label>
          <ModelSelect fullWidth className="mt-4" value={data.model} onChange={handleModelChange} models={models} />
        </div>

        {/* Custom Date Picker for storing data - ONLY for effective_real */}
        {url === "effective_real" && (
          <div className="mb-4">
            <label className="block font-semibold">Date de saisie des données</label>
            <FrenchDatePicker
              selected={data.custom_date}
              onChange={(date) => handleDateChange(date, "custom_date")}
              dateFormat="yyyy-MM-dd"
              className="outline-0 p-[.5rem] border border-[#b3b3b3] focus:border-2 focus:border-[#2684ff] rounded mt-4"
              placeholderText="Sélectionner une date"
            />
            <small className="text-gray-500 mt-1">
              Cette date sera utilisée pour stocker les données (par défaut: aujourd'hui)
            </small>
          </div>
        )}

        {url === "effective_standard" && (
          <>
            <div className="mb-4">
              <label className="block font-semibold">Date de début</label>
                          <FrenchDatePicker
              selected={data.start_date || null}
              onChange={(date) => handleDateChange(date, "start_date")}
              dateFormat="yyyy-MM-dd"
              className="outline-0 p-[.5rem] border border-[#b3b3b3] focus:border-2 focus:border-[#2684ff] rounded mt-4"
            />
            </div>

            <div className="mb-4">
              <label className="block font-semibold">Date de fin</label>
                          <FrenchDatePicker
              selected={data.end_date || null}
              onChange={(date) => handleDateChange(date, "end_date")}
              dateFormat="yyyy-MM-dd"
              className="outline-0 p-[.5rem] border border-[#b3b3b3] focus:border-2 focus:border-[#2684ff] rounded mt-4"
            />
            </div>
          </>
        )}

        <div className="mb-4">
          <label className="block font-semibold">Quenta</label>
          <div className="mt-4">
            <label className="mr-4">
              <input
                type="radio"
                name="cointa"
                value={true}
                checked={data.cointa === true || data.cointa === "true"}
                onChange={(e) => onChange("cointa", e.target.value === "true")}
              />
              Oui
            </label>
            <label className="mr-4">
              <input
                type="radio"
                name="cointa"
                value={false}
                checked={data.cointa === false || data.cointa === "false"}
                onChange={(e) => onChange("cointa", e.target.value === 0)}
              />
              Non
            </label>
          </div>
        </div>

        {data.cointa && data.cointa !== "false" && (
          <Input
            container="mb-4"
            label="Prix par pièce"
            type="number"
            name="price_by_part"
            text={data.price_by_part}
            handleChange={onChange}
          />
        )}

        <Input
          container="mb-4"
          label="Machinistes"
          type="number"
          name="machinistes"
          text={data.machinistes}
          handleChange={onChange}
        />

        <Input
          container="mb-4"
          label="Machinistes Stagiaires"
          type="number"
          name="machinistes_stagiaires"
          text={data.machinistes_stagiaires}
          handleChange={onChange}
        />

        <Input
          container="mb-4"
          label="Repassage Préparation"
          type="number"
          name="repassage_preparation"
          text={data.repassage_preparation}
          handleChange={onChange}
        />

        <Input
          container="mb-4"
          label="Trassage"
          type="number"
          name="trassage"
          text={data.trassage}
          handleChange={onChange}
        />

        <Input
          container="mb-4"
          label="Transport"
          type="number"
          name="transport"
          text={data.transport}
          handleChange={onChange}
        />

        <Input
          container="mb-4"
          label="Machines Spéciales"
          type="number"
          name="machines_speciales"
          text={data.machines_speciales}
          handleChange={onChange}
        />

        <Input
          container="mb-4"
          label="Trassage Spécial"
          type="number"
          name="trassage_special"
          text={data.trassage_special}
          handleChange={onChange}
        />

        <Input
          container="mb-4"
          label="Contrôle Table"
          type="number"
          name="controle_table"
          text={data.controle_table}
          handleChange={onChange}
        />

        <Input
          container="mb-4"
          label="Contrôle Final"
          type="number"
          name="controle_final"
          text={data.controle_final}
          handleChange={onChange}
        />

        <Input
          container="mb-4"
          label="Machinistes Retouche"
          type="number"
          name="machinistes_retouche"
          text={data.machinistes_retouche}
          handleChange={onChange}
        />

        <Input
          container="mb-4"
          label="Repassage Final"
          type="number"
          name="repassage_final"
          text={data.repassage_final}
          handleChange={onChange}
        />

        <Input
          container="mb-4"
          label="Finition"
          type="number"
          name="finition"
          text={data.finition}
          handleChange={onChange}
        />

        <Input
          container="mb-4"
          label="Autres"
          type="number"
          name="autre"
          text={data.autre}
          handleChange={onChange}
        />

        <Button classes="bg-blue-500 my-5 mb-8">
          {loading ? "Enregistrement ..." : effectiveStandardId ? "Mise à jour Effectif Direct" : "Enregistrer Effectif Direct"}
        </Button>

        {/* Clear Form button for effective_real */}
        {url === "effective_real" && (
          <Button classes="bg-gray-500 mb-8 mr-4" onClick={clearEntireForm}>
            Effacer le formulaire
          </Button>
        )}

        {effectiveStandardId && (
          <Button classes="bg-red-500 mb-8" onClick={handleDelete}>
            Supprimer Effectif Direct
          </Button>
        )}
      </form>
    </div>
  );
};

export default EffectiveDirect;
